import { EventSearchForm, EventSearchResponse, FieldNameEnum } from '@mark43/cad-api';
import { Table, TableContainer, Tbody, Td, Th, Thead, Tr, Checkbox, Text } from '@cad-arc';
import { CSSProperties } from 'react';
import { observer } from 'mobx-react-lite';
import { useBootstrapState } from '../bootstrap/bootstrap-provider';
import { formatMonthDayYearHoursMinutes } from '../common/helpers/utils';
import { EventSearchResultsUnitsCell } from './event-search-results-units-cell';
import { EventSearchResultsInvolvedEntitiesCell } from './event-search-results-involved-entities-cell';
import { EventSearchResultsReportingEventNumbersCell } from './event-search-results-reporting-event-number-cell';
import { EventSearchResultsEventNumberCell } from './event-search-results-event-number-cell';
import { ResultSelectionStore } from '../search-windows/use-observable-search-results-selection-store';

export const EventSearchResultsTable = observer(function EventSearchResultsTable({
    eventSearchResponse,
    selection,
    customStyle,
    searchData,
}: Readonly<{
    eventSearchResponse?: EventSearchResponse;
    selection: ResultSelectionStore;
    customStyle?: CSSProperties;
    searchData?: EventSearchForm;
}>) {
    const bootstrapState = useBootstrapState();
    const rows = eventSearchResponse?.eventSearchRows ?? [];

    return (
        <TableContainer>
            <Table
                size="md"
                variant="accented"
                width="100%"
                {...(customStyle ? { style: customStyle } : {})}
            >
                <Thead>
                    <Tr>
                        <Th key="select">
                            <Checkbox
                                isChecked={
                                    Object.keys(selection.selectedItems).length === rows.length
                                }
                                onChange={(e) => {
                                    if (e.target.checked) {
                                        selection.selectAllOnPage();
                                    } else {
                                        selection.unselectAll();
                                    }
                                }}
                            />
                        </Th>
                        <Th key="event-date">Event Date</Th>
                        <Th key="event-number">Event Number</Th>
                        <Th key="reporting-event-numbers">
                            {
                                bootstrapState.fieldDisplayNameByFieldName[
                                    FieldNameEnum.AGENCY_EVENT_REPORTING_EVENT_NUMBER.name
                                ]
                            }
                        </Th>
                        <Th key="call-for-service">Call For Service</Th>
                        <Th key="event-location">Event Location</Th>
                        <Th key="units">Units</Th>
                        <Th key="involved-persons">Involved Persons</Th>
                        <Th key="involved-vehicles">Involved Vehicles</Th>
                    </Tr>
                </Thead>
                <Tbody>
                    {rows.map((row) => (
                        <Tr key={row.eventNumber}>
                            <Td key="select">
                                <Checkbox
                                    isChecked={!!selection.selectedItems[row.eventId]}
                                    onChange={() => selection.toggleSelected(row.eventId)}
                                />
                            </Td>
                            <Td key="event-date">
                                {formatMonthDayYearHoursMinutes(row.eventDate)}
                            </Td>
                            <Td key="event-number">
                                <EventSearchResultsEventNumberCell
                                    eventSearchRow={row}
                                    searchData={searchData}
                                />
                            </Td>
                            <Td key="reporting-event-numbers">
                                <EventSearchResultsReportingEventNumbersCell eventSearchRow={row} />
                            </Td>
                            <Td key="call-for-service">
                                <Text style={{ width: '139px', whiteSpace: 'normal' }}>
                                    {row.callForService}
                                </Text>
                            </Td>
                            <Td key="event-location">
                                <Text style={{ width: '218px', whiteSpace: 'normal' }}>
                                    {row.eventLocation}
                                </Text>
                            </Td>
                            <Td key="units">
                                <EventSearchResultsUnitsCell eventSearchRow={row} />
                            </Td>
                            <Td key="involved-persons">
                                <EventSearchResultsInvolvedEntitiesCell
                                    entities={row.persons}
                                    eventId={row.eventId}
                                    eventPanelTab="PERSONS"
                                />
                            </Td>
                            <Td key="involved-vehicles">
                                <EventSearchResultsInvolvedEntitiesCell
                                    entities={row.vehicles}
                                    eventId={row.eventId}
                                    eventPanelTab="VEHICLES"
                                />
                            </Td>
                        </Tr>
                    ))}
                </Tbody>
            </Table>
        </TableContainer>
    );
});
